/**
 * What shared code may ask of the device's outbox. The outbox itself is the
 * app's (SQLite, sync runner), which shared-logic cannot import, so the app
 * supplies this through `GrippContext`; web provides nothing and every caller
 * falls back to its direct path.
 */

import type { ReportIssueInput } from '../graphql/types';
import type { Location } from '../location/domain';

/** The record kinds a form can hand to the outbox. */
export type OfflineSubmitKind =
  | 'createAssetRoutine'
  | 'reportIssue'
  | 'addAsset'
  | 'tagScan';

interface IdRef {
  id: string;
}

/** A photo or signature captured to the device, to be uploaded before the submission that references it. */
export interface OfflineMediaRef {
  id: string;
  name: string;
  extension: string;
  categories?: IdRef[];
}

/** Today's `CreateAssetRoutineInput` as the routine form builds it, plus the client id. */
export interface AssetRoutineSubmission {
  input: {
    id: string;
    asset: IdRef;
    workspaceAsset: IdRef;
    workspace: IdRef;
    routine: IdRef;
    routineVersion: IdRef;
    data: unknown;
    location?: unknown;
    createdFrom?: string;
    sourceIssue?: IdRef;
  };
  /** When the user submitted; the routine keeps this as its creation time however late it is sent. */
  capturedAt: Date | number;
  /** The server draft the form had loaded, if any; only that draft is deleted once the routine is sent. */
  loadedDraft?: { id: string; updatedAt?: string | null } | null;
  /** Local photos and signatures the answers reference by id. */
  media?: OfflineMediaRef[];
}

/**
 * The reminder a report carries, as `createWorkflow` takes it minus the issue
 * it belongs to: that is the report's own id, which the submission fills in.
 */
export interface ReminderSubmission {
  /** Client-chosen v4 id of the workflow. */
  id: string;
  input: {
    name: string;
    workspace: IdRef;
    trigger: { type: string; config: Record<string, unknown> };
    action: { type: string };
  };
}

/** Today's `ReportIssueInput` as the report form builds it, with the client id the report keeps. */
export interface ReportIssueSubmission {
  input: ReportIssueInput & { id: string };
  /** When the user submitted; the report keeps this as its creation date unless the user picked another. */
  capturedAt: Date | number;
  /** Created once the report is, and never before it. */
  reminder?: ReminderSubmission;
  /** Local photos the report's `media` ids reference. */
  media?: OfflineMediaRef[];
}

/** Today's `AddAssetInput` as the asset form builds it, with the client ids of the asset and its workspace asset. */
export interface AddAssetSubmission {
  input: {
    id: string;
    workspaceAssetId: string;
    name: string;
    workspaceId: string;
    tagId?: string;
    make?: string;
    model?: string;
    description?: string;
    details?: string;
    coverImageId?: string;
    mediaIds?: string[];
    lastLocation?: unknown;
    groupIds?: string[];
    product?: string;
    serialNumber?: string;
    addons?: string[];
  };
  /** When the user submitted; becomes the asset's `createdAt`. */
  capturedAt: Date | number;
  /** Local photos `coverImageId` and `mediaIds` reference. */
  media?: OfflineMediaRef[];
}

/**
 * A tag scan: the id and capture time belong to the scan attempt, so a retry
 * (or the same scan sent twice) is one scan on the server.
 */
export interface TagScanSubmission {
  /** Client-chosen v4 id of the scan. */
  id: string;
  tagId: string;
  tagCode?: string;
  /** When the tag was scanned; the scan keeps it however late it is sent. */
  capturedAt: Date | number;
  location?: Location;
}

export interface OfflineSubmissions {
  createAssetRoutine: AssetRoutineSubmission;
  reportIssue: ReportIssueSubmission;
  addAsset: AddAssetSubmission;
  tagScan: TagScanSubmission;
}

/**
 * What `submit` resolves to: the work is durably accepted. `rowId` names the
 * outbox row (observe it through `useOutbox`); it is null when the direct
 * mutation sent the work instead and there is nothing left to observe.
 */
export interface SubmitResult {
  status: 'accepted';
  rowId: string | null;
  /**
   * Direct path only: the report was saved but its reminder was not. The
   * submission still counts as accepted, since retrying cannot help a caller
   * the server refuses reminders from and would re-send the report.
   */
  reminderFailed?: boolean;
}

/** The outbox cannot take work right now (no store, or no sync runner mounted); the caller sends directly. */
export class OutboxUnavailableError extends Error {
  constructor() {
    super('The offline outbox is not available.');
    this.name = 'OutboxUnavailableError';
  }
}

/** A direct server action was refused because an unsent submission still touches the record. */
export class SyncLockedError extends Error {
  constructor() {
    super('This item is still syncing.');
    this.name = 'SyncLockedError';
  }
}

/** The user chose to keep unsynced work rather than lose it, so the sign-out or account swap did not happen. */
export class UnsyncedWorkCancelledError extends Error {
  constructor() {
    super('Unsynced work is still on this device.');
    this.name = 'UnsyncedWorkCancelledError';
  }
}

export interface OutboxService {
  /**
   * Whether `submit` can take work right now: signed in, with a store and a
   * mounted sync runner. Photos may only be captured to the device when this
   * holds, because otherwise `submit` falls back to a direct send and a local
   * photo id would never be uploaded.
   */
  canAccept: () => boolean;
  /** Queues the submission durably and starts a sync run; rejects with `OutboxUnavailableError` when it cannot. */
  submit: <K extends OfflineSubmitKind>(
    kind: K,
    payload: OfflineSubmissions[K]
  ) => Promise<SubmitResult>;
  /**
   * Queues uploads for local photos that something kept outside the outbox
   * (a server draft) references, so they are uploaded rather than swept as
   * orphans. A later submission that references them waits for them. Rejects
   * with `OutboxUnavailableError` when it cannot.
   */
  submitMedia: (media: OfflineMediaRef[], workspaceId: string) => Promise<void>;
  /** Where a photo still waiting to upload lives on this device, or null once it is gone (uploaded or discarded). */
  localMediaUri: (mediaId: string) => string | null;
  /**
   * Called before the session's account is dropped or swapped. Resolves true
   * when nothing unsynced is left behind (none existed, or the user synced or
   * discarded it) and false when the user chose to stay.
   */
  confirmLeave: () => Promise<boolean>;
  /** Whether an unresolved submission still touches any of these server records; read fresh, for dispatch-time checks. */
  isLocked: (entityIds: readonly string[]) => Promise<boolean>;
}
